import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Input, Modal, Select, Spin, Table, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import get from 'lodash.get';
import { format } from 'date-fns';
import { GET } from '../../../../service/base-api-service';
import { getIssueLists } from '../../../../utils/utils';
import issueSolutions from '../../../../utils/issues_solutions.json';
import {
  getConsumablesExpiryRows,
  getGasSensorExpiryRows,
  formatAsrDate,
  isAsrDatePast,
} from '../../../../utils/asr-service-form-data';

const { TextArea } = Input;

const toReportId = (report, index) =>
  report?.id || report?.serviceFormId || report?.fileName || report?.name || `report-${index}`;

const getReportDate = (report) =>
  report?.servicedDate || report?.serviceDate || report?.reportDate || report?.createdAt || report?.createdDate || null;

const getNextServiceDate = (serviceForm, chamber, details) =>
  get(serviceForm, 'nextServiceDate') ||
  get(serviceForm, 'nextServiceDueDate') ||
  get(serviceForm, 'nextServiceDue') ||
  get(serviceForm, 'service.nextServiceDate') ||
  chamber?.nextServiceDate ||
  chamber?.nextServiceDueDate ||
  details?.nextServiceDate ||
  details?.nextServiceDueDate ||
  null;

const fetchChamberDetails = async (chamberId) => {
  const urls = [`v1/admin/chamber/${chamberId}`, `v1/chamber/${chamberId}`];
  for (const url of urls) {
    try {
      const data = await GET(url);
      if (data) return data;
    } catch (err) {
      // Try the next endpoint.
    }
  }
  return {};
};

const priorityTag = (priority) => {
  if (priority === 'HIGH') return <Tag color="error">High</Tag>;
  if (priority === 'MEDIUM') return <Tag color="warning">Medium</Tag>;
  return <Tag color="success">Low</Tag>;
};

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const getWords = (value) => new Set(normalizeText(value).split(' ').filter((word) => word.length > 2));

const getSimilarity = (left, right) => {
  const leftWords = getWords(left);
  const rightWords = getWords(right);
  if (!leftWords.size || !rightWords.size) return 0;

  const matchingWords = [...leftWords].filter((word) => rightWords.has(word)).length;
  return matchingWords / Math.max(leftWords.size, rightWords.size);
};

const solutionCatalog = Object.entries(issueSolutions).map(([question, answers]) => ({
  normalizedQuestion: normalizeText(question),
  answers: Object.entries(answers).map(([issue, details]) => ({ issue, ...details })),
}));

const getSuggestedSolution = (issue) => {
  if (issue.note || !issue.selectedAnswer) return '';

  const normalizedIssue = normalizeText(issue.selectedAnswer);
  const exactIssue = solutionCatalog
    .flatMap((entry) => entry.answers)
    .find((entry) => normalizeText(entry.issue) === normalizedIssue);
  if (exactIssue) return exactIssue.solution || '';

  const questionGroup = solutionCatalog.find(
    (entry) => entry.normalizedQuestion === normalizeText(issue.question),
  );
  if (!questionGroup) return '';
  if (questionGroup.answers.length === 1) return questionGroup.answers[0].solution || '';

  const closest = questionGroup.answers
    .map((entry) => ({ ...entry, similarity: getSimilarity(issue.selectedAnswer, entry.issue) }))
    .sort((a, b) => b.similarity - a.similarity)[0];

  return closest?.similarity >= 0.25 ? closest.solution || '' : '';
};

const BatteryVoltages = ({ values }) => {
  const readings = (values || []).filter(
    (value) => value !== null && value !== undefined && String(value).trim() !== '',
  );

  return (
    <div className="asr-battery-readings">
      <div className="asr-battery-readings-title">Battery Voltages</div>
      {readings.length ? (
        <div className="asr-battery-readings-grid">
          {readings.map((value, index) => {
            const numericValue = Number.parseFloat(value);
            const isLow = Number.isFinite(numericValue) && numericValue < 12.5;

            return (
              <div className="asr-battery-reading" key={`${index}-${value}`}>
                <span>Battery {index + 1}</span>
                <strong className={isLow ? 'low' : ''}>{value}</strong>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="asr-battery-readings-empty">No battery voltages recorded in the latest service form.</div>
      )}
    </div>
  );
};

const ChamberInformation = ({ chamber, details, serviceForm }) => {
  const fields = [
    ['MA ID', details?.chamberID || details?.masaNumber || chamber?.chamberName],
    ['Location', serviceForm?.chamberLocation || 'Not applicable'],
    ['Size', details?.capacity || details?.size || 'Not recorded'],
    [
      'Scrubber Type',
      get(details, 'scrubberTypeEnum.displayName') ||
        get(details, 'scrubberTypeEnum.name') ||
        details?.scrubberType ||
        'Not recorded',
    ],
      ['Next Service Due', formatAsrDate(getNextServiceDate(serviceForm, chamber, details))],
  ];

  return (
    <div className="asr-chamber-information">
      <div className="asr-chamber-information-title">Chamber Details</div>
      <div className="asr-chamber-information-grid">
        {fields.map(([label, value]) => (
          <div className="asr-chamber-information-item" key={label}>
            <span>{label}</span>
            <strong className={label === 'Next Service Due' && isAsrDatePast(getNextServiceDate(serviceForm, chamber, details)) ? 'overdue' : ''}>
              {value || 'Not recorded'}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const ExpiryLogs = ({ serviceForm }) => {
  const consumableRows = getConsumablesExpiryRows(serviceForm);
  const gasSensorRows = getGasSensorExpiryRows(serviceForm);

  return (
    <>
      {gasSensorRows.length > 0 && (
        <div className="asr-expiry-log">
          <div className="asr-chamber-information-title">Gas Sensors Expiry Log</div>
          <div className="asr-expiry-log-header gas-sensors">
            <span>System</span>
            <span>Sensor</span>
            <span>Calibration Due Date</span>
            <span>Expiry Date</span>
          </div>
          {gasSensorRows.map((row, index) => (
            <div className="asr-expiry-log-row gas-sensors" key={`${row.system}-${row.sensor}-${index}`}>
              <strong>{row.system}</strong>
              <span>{row.sensor}</span>
              <span className={isAsrDatePast(row.calibrationDateValue) ? 'overdue' : ''}>{row.calibrationDate}</span>
              <span className={isAsrDatePast(row.expiryDateValue) ? 'overdue' : ''}>{row.expiryDate}</span>
            </div>
          ))}
        </div>
      )}

      {consumableRows.length > 0 && (
        <div className="asr-expiry-log">
          <div className="asr-chamber-information-title">Consumables Expiry Log</div>
          <div className="asr-expiry-log-header consumables">
            <span>Consumable</span>
            <span>Expiry Date</span>
          </div>
          {consumableRows.map((row) => (
            <div className="asr-expiry-log-row consumables" key={row.consumable}>
              <strong>{row.consumable}</strong>
              <span className={isAsrDatePast(row.expiryDateValue) ? 'overdue' : ''}>{row.expiryDate}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const formatVisitDate = (value) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not recorded' : format(parsed, 'dd MMM yyyy');
};

const ReportDetails = ({ details, onChange, visitStart, visitEnd }) => {
  const readOnlyFields = new Set(['siteName', 'preparedBy', 'preparedByEmail', 'preparedByTelephone']);
  const fields = [
    ['siteName', 'Site'],
    ['attendeeName', 'ATT'],
    ['attendeeEmail', 'Email'],
    ['ccEmail', 'CC'],
    ['preparedBy', 'Report Prepared By'],
    ['preparedByEmail', 'Prepared By Email'],
    ['preparedByTelephone', 'TEL'],
  ];

  return (
    <div className="asr-report-details">
      <div className="asr-chamber-information-title">Report Details</div>
      <div className="asr-report-details-grid">
        {fields.map(([key, label]) => (
          <label className="asr-report-details-field" key={key}>
            <span>{label}</span>
            <Input
              value={details[key] || ''}
              placeholder="Not provided"
              disabled={readOnlyFields.has(key)}
              onChange={(event) => onChange({ ...details, [key]: event.target.value })}
            />
          </label>
        ))}
        <label className="asr-report-details-field">
          <span>Date On Site</span>
          <Input value={`${formatVisitDate(visitStart)} - ${formatVisitDate(visitEnd)}`} disabled />
        </label>
        <label className="asr-report-details-field">
          <span>Quote #</span>
          <Input
            value={details.quoteNumber || ''}
            placeholder="Enter quote number"
            onChange={(event) => onChange({ ...details, quoteNumber: event.target.value })}
          />
        </label>
      </div>
    </div>
  );
};

// Fetches the latest after-service report for a chamber and extracts its issue list,
// then lets the technician type a solution against each issue for this ASR session.
const AsrChamberIssueEditor = ({
  visible,
  chamber,
  tenantId,
  reportDetails,
  visitStart,
  visitEnd,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceReport, setSourceReport] = useState(null);
  const [chamberDetails, setChamberDetails] = useState({});
  const [latestServiceForm, setLatestServiceForm] = useState({});
  const [batteryVoltages, setBatteryVoltages] = useState([]);
  const [issues, setIssues] = useState([]);
  const [editableReportDetails, setEditableReportDetails] = useState(reportDetails || {});
  const [saving, setSaving] = useState(false);
  const manualIssueSequence = useRef(0);

  useEffect(() => {
    if (visible) setEditableReportDetails(reportDetails || {});
  }, [visible, reportDetails]);

  useEffect(() => {
    if (!visible || !chamber) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      setSourceReport(null);
      setChamberDetails({});
      setLatestServiceForm({});
      setBatteryVoltages([]);
      const chamberDetailsPromise = fetchChamberDetails(chamber.chamberId);

      if (chamber.latestServiceForm) {
        const detailed = chamber.latestServiceForm;
        const details = await chamberDetailsPromise;
        if (cancelled) return;

        setChamberDetails({ ...chamber, ...details });
        setLatestServiceForm(detailed);
        setSourceReport({
          servicedDate: detailed.servicedDate || chamber.sourceServicedDate || null,
          chamberName: detailed.chamberName || chamber.chamberName,
        });
        setBatteryVoltages(
          chamber.batteryVoltages ||
            get(detailed, 'consumables.batteryVoltages.recordBatteryVoltages', []) ||
            [],
        );

        const { issuesList = [] } = getIssueLists(detailed);
        const existingByQuestion = new Map((chamber.issues || []).map((issue) => [issue.question, issue]));
        setIssues([
          ...issuesList.map((issue, index) => ({
            ...issue,
            rowId: existingByQuestion.get(issue.question)?.rowId || issue.question || `source-${index}`,
            solution: existingByQuestion.get(issue.question)?.solution || getSuggestedSolution(issue),
          })),
          ...(chamber.issues || []).filter((issue) => issue.isManual),
        ]);
        setLoading(false);
        return;
      }

      const attempts = [
        `v1/admin/report/chambers/${chamber.chamberId}/reports?type=after-service`,
        `v1/report/chambers/${chamber.chamberId}/reports?type=after-service`,
      ];

      let reports = [];
      for (const url of attempts) {
        try {
          const payload = await GET(url);
          const rows = Array.isArray(payload) ? payload : payload?.results || payload?.list || [];
          if (rows.length) {
            reports = rows;
            break;
          }
        } catch (err) {
          // try next endpoint
        }
      }

      if (!reports.length) {
        if (!cancelled) {
          setChamberDetails(await chamberDetailsPromise);
          setIssues(
            (chamber.issues || []).map((issue) => ({
              ...issue,
              rowId: issue.rowId || issue.question,
              solution: issue.solution || getSuggestedSolution(issue),
            })),
          );
          setLoading(false);
        }
        return;
      }

      const sorted = [...reports].sort(
        (a, b) => new Date(getReportDate(b) || 0).getTime() - new Date(getReportDate(a) || 0).getTime(),
      );
      const latest = sorted[0];
      const serviceFormId = latest?.serviceFormId || toReportId(latest, 0);

      try {
        const [detailed, details] = await Promise.all([
          GET(`v1/admin/report/serviceForm/${serviceFormId}?tenantId=${tenantId}`),
          chamberDetailsPromise,
        ]);
        if (cancelled) return;

        setChamberDetails(details);
        setLatestServiceForm(detailed);
        setSourceReport({
          servicedDate: getReportDate(latest),
          chamberName: get(detailed, 'chamberName') || chamber.chamberName,
        });
        setBatteryVoltages(
          get(detailed, 'consumables.batteryVoltages.recordBatteryVoltages', []) || [],
        );

        const { issuesList } = getIssueLists(detailed);
        const existingByQuestion = new Map((chamber.issues || []).map((i) => [i.question, i]));

        setIssues([
          ...(issuesList || []).map((issue, index) => ({
            ...issue,
            rowId: existingByQuestion.get(issue.question)?.rowId || issue.question || `source-${index}`,
            solution: existingByQuestion.get(issue.question)?.solution || getSuggestedSolution(issue),
          })),
          ...(chamber.issues || []).filter((issue) => issue.isManual),
        ]);
      } catch (err) {
        if (!cancelled) {
          setError('Unable to load the latest service form for this chamber. You can still add solutions manually below.');
          setIssues(
            (chamber.issues || []).map((issue) => ({
              ...issue,
              rowId: issue.rowId || issue.question,
              solution: issue.solution || getSuggestedSolution(issue),
            })),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, chamber?.chamberId]);

  const updateIssue = (rowId, field, value) => {
    setIssues((previous) => previous.map((issue) => (issue.rowId === rowId ? { ...issue, [field]: value } : issue)));
  };

  const addIssue = () => {
    const rowId = `manual-${Date.now()}-${manualIssueSequence.current++}`;
    setIssues((previous) => [
      ...previous,
      { rowId, question: rowId, priority: 'LOW', selectedAnswer: '', solution: '', isManual: true },
    ]);
  };

  const removeIssue = (rowId) => {
    setIssues((previous) => previous.filter((issue) => issue.rowId !== rowId));
  };

  const handleSave = () => {
    setSaving(true);
    onSave(chamber.chamberId, {
      issues,
      sourceServicedDate: sourceReport?.servicedDate || chamber.sourceServicedDate || null,
      reportDetails: editableReportDetails,
    });
    setSaving(false);
    message.success(`Issues saved for ${chamber?.chamberName}`);
    onClose();
  };

  const columns = [
      {
        title: 'Priority',
        dataIndex: 'priority',
        width: 100,
        render: (value, record) => record.isManual ? (
          <Select value={value || 'LOW'} onChange={(priority) => updateIssue(record.rowId, 'priority', priority)}>
            <Select.Option value="LOW">Low</Select.Option>
            <Select.Option value="MEDIUM">Medium</Select.Option>
            <Select.Option value="HIGH">High</Select.Option>
          </Select>
        ) : priorityTag(value),
      },
      {
        title: 'Issue',
        dataIndex: 'selectedAnswer',
        render: (val, record) => record.isManual ? (
          <Input
            value={val}
            placeholder="Describe the issue"
            onChange={(event) => updateIssue(record.rowId, 'selectedAnswer', event.target.value)}
          />
        ) : (
          <div>
            <div className="asr-issue-text">{val}</div>
            {record.note && <div className="asr-issue-note">Note: {record.note}</div>}
          </div>
        ),
      },
      {
        title: 'Solution',
        dataIndex: 'solution',
        width: 360,
        render: (val, record) => (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            placeholder="Type the solution applied for this issue..."
            value={val}
            onChange={(e) => updateIssue(record.rowId, 'solution', e.target.value)}
          />
        ),
      },
        {
          title: '',
          key: 'actions',
          width: 92,
          render: (_, record) => record.isManual ? (
            <Button
              danger
              className="asr-remove-issue-btn"
              onClick={() => removeIssue(record.rowId)}
            >
              Remove
            </Button>
          ) : null,
        },
      ];

  return (
    <Modal
      title={(
        <div className="asr-modal-title-wrap">
          <div className="asr-modal-title">Chamber Issues</div>
          <div className="asr-modal-subtitle">
            Issues and solutions for <strong>{chamber?.chamberName || 'this chamber'}</strong>
          </div>
        </div>
      )}
      visible={visible}
      width={1120}
      centered
      onCancel={onClose}
      wrapClassName="site-asr-modal-wrap"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave} disabled={loading}>
          Save Issues & Solutions
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <div className="site-asr-modal-loading">
          <Spin />
        </div>
      ) : (
        <div>
          {sourceReport?.servicedDate && (
            <Alert
              type="info"
              showIcon
              className="asr-issue-source-alert"
              message={`Issues pulled from the service form dated ${format(new Date(sourceReport.servicedDate), 'dd/MM/yyyy')}`}
            />
          )}
          {error && <Alert type="warning" showIcon message={error} className="asr-issue-source-alert" />}
          <ReportDetails
            details={editableReportDetails}
            onChange={setEditableReportDetails}
            visitStart={visitStart}
            visitEnd={visitEnd}
          />
          <ChamberInformation chamber={chamber} details={chamberDetails} serviceForm={latestServiceForm} />
          <BatteryVoltages values={batteryVoltages} />
          <ExpiryLogs serviceForm={latestServiceForm} />
          <div className="asr-issue-table-actions">
            <Button icon={<PlusOutlined />} onClick={addIssue}>Add Issue</Button>
          </div>
          <Table
            rowKey="rowId"
            columns={columns}
            dataSource={issues}
            pagination={false}
            scroll={{ y: 420 }}
            locale={{ emptyText: <Empty description="No open issues found on the latest service form." /> }}
          />
        </div>
      )}
    </Modal>
  );
};

export default AsrChamberIssueEditor;
